import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAllContent, trackPageView } from '../services/api';
import * as mockData from '../data/mock';

const ContentContext = createContext(null);

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const data = await fetchAllContent();
        
        if (data) {
          setContent(data);
          // Track page view on successful load
          trackPageView('/');
        } else {
          // Fallback to mock data if API fails
          console.log('Using fallback mock data');
          setContent({
            siteConfig: mockData.siteConfig,
            heroContent: mockData.heroContent,
            whyCommunicationContent: mockData.whyCommunicationContent,
            frameworkContent: mockData.frameworkContent,
            programsContent: mockData.programsContent,
            finalCtaContent: mockData.finalCtaContent,
            footerContent: mockData.footerContent,
            navItems: mockData.navItems,
          });
          trackPageView('/');
        }
      } catch (err) {
        console.error('Failed to load content:', err);
        setError(err.message);
        // Fallback to mock data
        setContent({
          siteConfig: mockData.siteConfig,
          heroContent: mockData.heroContent,
          whyCommunicationContent: mockData.whyCommunicationContent,
          frameworkContent: mockData.frameworkContent,
          programsContent: mockData.programsContent,
          finalCtaContent: mockData.finalCtaContent,
          footerContent: mockData.footerContent,
          navItems: mockData.navItems,
        });
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  const value = {
    content,
    loading,
    error,
    // Individual content accessors with fallbacks
    siteConfig: content?.siteConfig || mockData.siteConfig,
    heroContent: content?.heroContent || mockData.heroContent,
    whyCommunicationContent: content?.whyCommunicationContent || mockData.whyCommunicationContent,
    frameworkContent: content?.frameworkContent || mockData.frameworkContent,
    programsContent: content?.programsContent || mockData.programsContent,
    finalCtaContent: content?.finalCtaContent || mockData.finalCtaContent,
    footerContent: content?.footerContent || mockData.footerContent,
    navItems: content?.navItems || mockData.navItems,
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};

export default ContentContext;
